import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import PageHero from "../components/site/PageHero";
import { PageSection, CheckGrid, NumberSteps, CTABand, DisclaimerBox } from "../components/site/ContentBlocks";
import { serviceLd } from "../components/site/SEO";
import { FadeUp } from "../components/site/Reveal";
import { HEALTHCARE_SERVICES, PATIENT_JOURNEY, HEALTHCARE_DISCLAIMER, DESTINATIONS } from "../data/content";

export default function Healthcare() {
    return (
        <div className="grain bg-navy-deep" data-testid="healthcare-page">
            <Header />
            <main>
                <PageHero
                    seo={{
                        title: "International Healthcare & Medical Tourism Coordination | Viet Medica Ltd",
                        description:
                            "Viet Medica Ltd connects international patients with healthcare providers and coordinated medical-tourism support across Vietnam, India, Thailand and China.",
                        path: "/healthcare-medical-tourism",
                        jsonLd: serviceLd(
                            "International Healthcare and Medical Tourism Coordination",
                            "International patient coordination, provider connections and medical-travel support across Asia.",
                            "/healthcare-medical-tourism"
                        ),
                    }}
                    crumbs={[{ label: "Healthcare & Medical Tourism" }]}
                    eyebrow="Healthcare & Medical Tourism"
                    title="International Healthcare,"
                    titleEm="Made Easier to Navigate."
                    copy="Viet Medica Ltd connects international patients with healthcare providers and practical support across leading Asian medical destinations. We help patients understand available options, communicate with suitable providers and coordinate important parts of their journey."
                />

                <PageSection eyebrow="How We Support Patients" title="Coordination at Every Step" testid="healthcare-services">
                    <CheckGrid items={HEALTHCARE_SERVICES} cols={3} />
                </PageSection>

                <PageSection light eyebrow="Destinations" title="Explore Healthcare Destinations" testid="healthcare-destinations">
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {DESTINATIONS.map((d, i) => (
                            <FadeUp key={d.id} delay={i * 0.07} className="h-full">
                                <Link
                                    to={`/medical-tourism-${d.id}`}
                                    data-testid={`healthcare-destination-${d.id}`}
                                    className="group relative flex h-[340px] flex-col justify-end overflow-hidden border border-navy-deep/10"
                                >
                                    <img
                                        src={d.image}
                                        alt={d.imageAlt}
                                        loading="lazy"
                                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-[1.07]"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/40 to-navy-deep/5" aria-hidden="true" />
                                    <div className="relative flex items-end justify-between p-6">
                                        <h3 className="font-serif text-3xl font-light text-ivory">{d.name}</h3>
                                        <ArrowUpRight size={18} className="text-gold transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
                                    </div>
                                    <span className="absolute bottom-0 left-0 h-[2px] w-full origin-left scale-x-0 bg-gold transition-transform duration-700 group-hover:scale-x-100" aria-hidden="true" />
                                </Link>
                            </FadeUp>
                        ))}
                    </div>
                </PageSection>

                <PageSection eyebrow="Patient Journey" title="How the Patient Journey Works" testid="healthcare-journey">
                    <NumberSteps steps={PATIENT_JOURNEY} />
                </PageSection>

                <section className="bg-navy-deep pb-20 lg:pb-28">
                    <div className="mx-auto max-w-[1440px] space-y-10 px-5 lg:px-10">
                        <DisclaimerBox text={HEALTHCARE_DISCLAIMER} testid="healthcare-disclaimer" />
                        <CTABand
                            title="Tell us what support you need."
                            primary={{ label: "Healthcare Enquiry", to: "/healthcare-enquiry", testid: "healthcare-enquiry-cta" }}
                            secondary={{ label: "International Patient Support", to: "/patient-support", testid: "patient-support-cta" }}
                        />
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
